import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import type { ApiError } from "@procureflow/shared-types";
import { useAuth } from "@/contexts/auth-context";
import { fetchWithAuth, bootstrapSession } from "@/lib/auth-client";
import { ChangePasswordForm, type ChangePasswordPayload } from "@/components/change-password-form";

// US-055 — change password screen.
//
// Serves the forced first-login flow (a directly-created account carries
// must_change_password=true and AppLayout locks the user here until it is
// cleared) and ordinary self-service changes. On success the session is
// re-bootstrapped so the cleared flag releases the AppLayout lock, then
// the user lands on the dashboard.
//
// The current/new/confirm fields live in the reusable <ChangePasswordForm>
// (US-076) so the account-menu self-service dialog shares the same body,
// validation, and server-error surface.

export const Route = createFileRoute("/change-password")({
  head: () => ({
    meta: [{ title: "Change password — ProcureFlow" }],
  }),
  component: Page,
});

function Page() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const forced = user?.must_change_password ?? false;

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  async function handleSubmit(payload: ChangePasswordPayload) {
    setSubmitting(true);
    setError(undefined);
    try {
      const res = await fetchWithAuth("/api/auth/change-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as Partial<ApiError>;
        setError(body?.message ?? "Failed to change password.");
        return;
      }
      // Refresh the session so must_change_password flips to false and
      // AppLayout releases the lock.
      await bootstrapSession();
      await navigate({ to: "/" });
    } catch {
      setError("An unexpected error occurred.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="w-full max-w-md rounded-2xl bg-card border border-border shadow-elegant p-6 space-y-4">
        <div>
          <h1 className="text-lg font-semibold tracking-tight">Change your password</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {forced
              ? "Set a new password before continuing. You won't be able to use the app until this is done."
              : "Choose a new password for your account."}
          </p>
        </div>

        <ChangePasswordForm onSubmit={handleSubmit} submitting={submitting} error={error} />
      </div>
    </div>
  );
}
